import React from "react";
import { FiCheck, FiClock, FiX } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { UserStatus } from "../../../types/userInvite";

interface StatusConfig {
  colors: string;
  icon: React.ReactNode;
  label: string;
}

const STATUS_CONFIG: Record<UserStatus, StatusConfig> = {
  active: {
    icon: <FiCheck size={10} aria-hidden="true" />,
    label: "Active",
    colors: "bg-green-100 text-green-700",
  },
  invited: {
    icon: <FiClock size={10} aria-hidden="true" />,
    label: "Invited",
    colors: "bg-amber-100 text-amber-700",
  },
  deactivated: {
    icon: <FiX size={10} aria-hidden="true" />,
    label: "Deactivated",
    colors: "bg-gray-100 text-gray-600",
  },
};

interface UserStatusBadgeProps {
  className?: string;
  inviteExpiresAt?: string | null;
  status: UserStatus;
}

const formatExpiry = (expiresAt: string): string => {
  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    return "Expired";
  }

  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHrs > 24) {
    const days = Math.floor(diffHrs / 24);
    return `Expires in ${days} day${days !== 1 ? "s" : ""}`;
  }

  if (diffHrs > 0) {
    return `Expires in ${diffHrs}h ${diffMins}m`;
  }

  return `Expires in ${diffMins}m`;
};

export const UserStatusBadge: React.FC<UserStatusBadgeProps> = ({
  status,
  inviteExpiresAt,
  className = "",
}) => {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.active;
  const tooltipText =
    status === "invited" && inviteExpiresAt ? formatExpiry(inviteExpiresAt) : undefined;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
        config.colors,
        className
      )}
      data-testid={`user-status-badge-${status}`}
      title={tooltipText}
    >
      {config.icon}
      {config.label}
      {tooltipText && (
        <span className="sr-only">{tooltipText}</span>
      )}
    </span>
  );
};
